import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { API } from "../../services/api";
import { useAuth } from "../../auth/AuthContext";
import { StatutMedicamentLabels, StatutMedicamentColors } from "../../models/statutMedicament.map";
import { defaultLabelDate, LabelPreview, openLabelPrintWindow } from "./deliveryLabels";
import {
    STATUTS_BON,
    HISTORIQUE_PAGE_SIZE,
    medicamentLineKey,
    isCnoLine,
    isPartialDeliverableLine,
    isDeliverySelectableLine,
    isDirectlyDeliverableLine,
    isReturnableToProductionLine,
    cnoBonFromLines,
    parseSelectIdentifier,
    buildDeliveryOccurrenceOptions,
    hasPharmacyIdentity,
    matchesDeliverySearch,
    makePatch,
    deliveryCardMetrics,
    splitBonByClassification,
    bonInitials,
    deliverySelectionHint,
    plural,
} from "./deliveryLines";
import { PRINT_GROUPING_MODES, generatePrintHTML, openPrintWindow } from "./deliveryPrint";
import { DeliveryPlanningTab } from "../../pages/adminDashboard/delivery/DeliveryPlanningTab";
import { TruckIcon } from "../../components/icons/InterfaceIcons";
import "./BonLivraison.css";

const DEFAULT_STATUTS = ["TRAITE", "EN_COMMANDE", "EN_RUPTURE"];

const inputStyle = {
    padding: "6px 8px",
    boxSizing: "border-box",
    borderRadius: "7px",
    border: "1px solid #cbd5e1",
    fontSize: "13px",
    background: "white",
    color: "#334155",
};

function bonLivraisonTabFromPath(pathname) {
    if (pathname === "/bon-livraison/historique") return "historique";
    if (pathname === "/bon-livraison/planning") return "planning";
    return "enCours";
}

function sameStatuts(a, b) {
    return a.length === b.length && a.every(statut => b.includes(statut));
}

/* ─── Styles partagés (modale d'envoi et historique) ──────────── */
const btnSecondary = {
    padding: "6px 14px", borderRadius: "7px", border: "1px solid #cbd5e1",
    background: "white", cursor: "pointer", fontSize: "13px", color: "#475569",
};
const btnPrimary = {
    padding: "6px 14px", borderRadius: "7px", border: "1px solid #1d4ed8",
    background: "#dbeafe", cursor: "pointer", fontSize: "13px", color: "#1d4ed8", fontWeight: 600,
};
const emptyState = { textAlign: "center", padding: "60px", color: "#94a3b8", fontSize: "14px" };

/* ─── En-tête du bon ouvert ────────────────────────────────────── */
function EhpadPanelHeader({ ehpad, hasStupVariant, onBack, onSelectVariant }) {
    const { lines, units, residents, statuses } = deliveryCardMetrics(ehpad);
    const waiting = (statuses.EN_COMMANDE || 0) + (statuses.EN_RUPTURE || 0);

    return (
        <header className="bl-panel-header">
            <button type="button" className="bl-back-button" onClick={onBack}>
                <span aria-hidden="true">←</span> Retour aux EHPAD
            </button>
            <span className={`bl-initials bl-initials--lg${ehpad.isStup ? " bl-initials--stup" : ""}`}>
                {bonInitials(ehpad)}
            </span>
            <div className="bl-panel-header__identity">
                <div className="bl-panel-header__title">
                    <h2>{ehpad.ehpadNom}</h2>
                    {ehpad.isStup && <span className="bl-badge bl-badge--stup">Stupéfiants</span>}
                </div>
                <p>{ehpad.ehpadCode}{ehpad.tourneeName ? ` · ${ehpad.tourneeName}` : ""}</p>
            </div>
            <dl className="bl-panel-header__metrics">
                <div><dt>Lignes</dt><dd>{lines}</dd></div>
                <div><dt>Unités</dt><dd>{units}</dd></div>
                <div><dt>Résidents</dt><dd>{residents}</dd></div>
                <div>
                    <dt>Prêts</dt>
                    <dd className={waiting > 0 ? "is-partial" : "is-ready"}>{statuses.TRAITE || 0}</dd>
                </div>
            </dl>
            {hasStupVariant && (
                <fieldset className="bl-variant-switch">
                    <legend className="bl-visually-hidden">Bon affiché</legend>
                    <button type="button" aria-pressed={!ehpad.isStup} onClick={() => onSelectVariant(false)}>
                        Bon standard
                    </button>
                    <button type="button" aria-pressed={ehpad.isStup} onClick={() => onSelectVariant(true)}>
                        Bon stupéfiants
                    </button>
                </fieldset>
            )}
        </header>
    );
}

/* ─── Vue cartes ───────────────────────────────────────────────── */
const CARD_STATUS_SEGMENTS = [
    { key: "TRAITE", tone: "ready", label: count => plural(count, "traité") },
    { key: "EN_COMMANDE", tone: "order", label: () => "en commande" },
    { key: "EN_RUPTURE", tone: "alert", label: count => plural(count, "rupture") },
    { key: "EN_LIVRAISON", tone: "transit", label: () => "en livraison" },
];

function BonEnCoursCard({ ehpad, onOpen }) {
    const { lines, units, residents, statuses } = deliveryCardMetrics(ehpad);
    const hasStup = ehpad.medicaments.some(med => med.classificationMedicament === "STUP");
    const segments = CARD_STATUS_SEGMENTS
        .map(segment => ({ ...segment, count: statuses[segment.key] || 0 }))
        .filter(segment => segment.count > 0);

    const headingId = `bl-card-title-${ehpad.ehpadId}`;

    return (
        <article className="bl-card" aria-labelledby={headingId}>
            <header className="bl-card__heading">
                <span className="bl-initials">{bonInitials(ehpad)}</span>
                <div className="bl-card__identity">
                    <h3 id={headingId}>
                        {/* Le bouton porte le code et étend sa zone de clic à toute la carte. */}
                        <button
                            type="button"
                            onClick={onOpen}
                            className="bl-card__open"
                            aria-label={`Ouvrir le bon ${ehpad.ehpadNom || ehpad.ehpadCode}`}
                            title={ehpad.ehpadNom}
                        >
                            {ehpad.ehpadCode || ehpad.ehpadNom}
                        </button>
                        {/* Le nom, affiché en petit sous le code, reste dans le titre lu par les lecteurs d'écran. */}
                        {ehpad.ehpadCode && <span className="bl-visually-hidden"> — {ehpad.ehpadNom}</span>}
                        {hasStup && <span className="bl-badge bl-badge--stup bl-badge--xs">Stup</span>}
                    </h3>
                    {ehpad.ehpadCode && <p className="bl-card__name" title={ehpad.ehpadNom} aria-hidden="true">{ehpad.ehpadNom}</p>}
                    {ehpad.tourneeName && <p>{ehpad.tourneeName}</p>}
                </div>
                <span className="bl-card__chevron" aria-hidden="true">›</span>
            </header>

            <p className="bl-card__summary">
                <strong>{lines}</strong> {plural(lines, "ligne")}
                <span aria-hidden="true"> · </span>
                <strong>{units}</strong> {plural(units, "unité")}
                <span aria-hidden="true"> · </span>
                <strong>{residents}</strong> {plural(residents, "résident")}
            </p>

            <ul className="bl-card__statuses">
                {segments.map(segment => (
                    <li key={segment.key} className={`bl-dot bl-dot--${segment.tone}`}>
                        {segment.count} {segment.label(segment.count)}
                    </li>
                ))}
            </ul>
        </article>
    );
}

/* ─── Aperçu d'impression ──────────────────────────────────────── */
const A4_PX_WIDTH  = 794;
const A4_PX_HEIGHT = 1123;

function PrintPreviewPanel({ ehpad, pharmacy, groupingMode, onClose }) {
    const wrapperRef = useRef(null);
    const [scale, setScale] = useState(0.45);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return undefined;
        const update = () => {
            const available = el.clientWidth - 24;
            if (available > 0) setScale(Math.min(1, available / A4_PX_WIDTH));
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const html = useMemo(
        () => generatePrintHTML(ehpad, null, pharmacy, ehpad.isStup, "", groupingMode),
        [ehpad, pharmacy, groupingMode]
    );

    return (
        <section className="bl-preview" aria-label="Aperçu d'impression">
            <div className="bl-preview__header">
                <span>Aperçu d'impression · A4</span>
                <button type="button" className="bl-icon-button" aria-label="Masquer l'aperçu" onClick={onClose}>×</button>
            </div>
            <div ref={wrapperRef} className="bl-preview__viewport">
                <div className="bl-preview__page" style={{ height: `${A4_PX_HEIGHT * scale}px` }}>
                    <div className="bl-preview__sheet" style={{ width: `${A4_PX_WIDTH}px`, height: `${A4_PX_HEIGHT}px`, transform: `scale(${scale})` }}>
                        <iframe
                            srcDoc={html}
                            style={{ width: `${A4_PX_WIDTH}px`, height: `${A4_PX_HEIGHT}px` }}
                            title="Aperçu bon de livraison"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ─── Tableau médicaments ──────────────────────────────────────── */
function HeaderCheckbox({ label, checked, indeterminate, disabled, onChange }) {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate;
    }, [indeterminate]);
    return (
        <input
            ref={ref}
            type="checkbox"
            className="bl-checkbox"
            aria-label={label}
            title={label}
            checked={checked}
            disabled={disabled}
            onChange={event => onChange(event.target.checked)}
        />
    );
}

function MedicamentTable({
    medicaments,
    onMarkNonLivre = null,
    includeById = null,
    onToggleInclude = null,
    onToggleAllIncluded = null,
    selectedDeliveryIds = null,
    onToggleDelivery = null,
    onToggleAllDelivery = null,
}) {
    const visible   = medicaments.filter(m => m.nom && m.nom.trim() !== "");
    const robot     = visible.filter(m => m.destination === "ROBOT");
    const etiq      = visible.filter(m => m.destination === "ETIQUETAGE");
    const autres    = visible.filter(m => m.destination !== "ROBOT" && m.destination !== "ETIQUETAGE");
    const hasSplit  = robot.length > 0 && etiq.length > 0;

    const hasActions = !!onMarkNonLivre;
    const showInclude = !!onToggleInclude;
    const showDeliverySelection = !!onToggleDelivery;
    const columnCount = 4 + Number(showInclude) + Number(showDeliverySelection) + Number(hasActions);

    const isIncluded = med => includeById?.[medicamentLineKey(med)] !== false;
    const isSelected = med => selectedDeliveryIds?.has(medicamentLineKey(med)) || false;
    const includedCount = visible.filter(isIncluded).length;
    const selectable = visible.filter(isDeliverySelectableLine);
    const selectedCount = selectable.filter(isSelected).length;

    const renderRows = (meds) => meds.map(med => {
        const lineKey = medicamentLineKey(med);
        const col = StatutMedicamentColors[med.statut] || { bg: "#f1f5f9", color: "#64748b" };
        const included = isIncluded(med);
        const deliverySelectable = isDeliverySelectableLine(med);
        const selectedForDelivery = isSelected(med);
        const rowClassName = [
            "bl-row",
            showInclude && !included ? "is-excluded" : "",
            selectedForDelivery ? "is-selected" : "",
        ].filter(Boolean).join(" ");
        return (
            <tr key={lineKey} className={rowClassName}>
                {showInclude && (
                    <td className="bl-cell-check">
                        <input
                            type="checkbox"
                            className="bl-checkbox"
                            aria-label={`Inclure ${med.nom} sur le bon`}
                            checked={included}
                            onChange={e => onToggleInclude(lineKey, e.target.checked)}
                        />
                    </td>
                )}
                <td className="bl-cell-resident">{med.patientNom} {med.patientPrenom}</td>
                <td className="bl-cell-product">
                    {med.nom}{med.dosage ? ` ${med.dosage}` : ""}
                    {med.destination === "ROBOT"      && <span className="bl-cell-product__mention">(Sachet)</span>}
                    {med.destination === "ETIQUETAGE" && <span className="bl-cell-product__mention">(Etiquette)</span>}
                    {showInclude && !included && <span className="bl-tag">Hors BL</span>}
                </td>
                <td className="bl-cell-qty">{med.quantite ?? "—"}</td>
                <td>
                    <span className="bl-status" style={{ background: col.bg, color: col.color }}>
                        {StatutMedicamentLabels[med.statut] || med.statut}
                    </span>
                </td>
                {showDeliverySelection && (
                    <td className="bl-cell-check bl-cell-check--selection">
                        <input
                            type="checkbox"
                            className="bl-checkbox bl-checkbox--selection"
                            aria-label={`Sélectionner ${med.nom} pour livraison`}
                            title={deliverySelectionHint(med)}
                            checked={selectedForDelivery}
                            disabled={!deliverySelectable}
                            onChange={e => onToggleDelivery(lineKey, e.target.checked)}
                        />
                    </td>
                )}
                {hasActions && (
                    <td className="bl-cell-actions">
                        {med.statut === "LIVRE" && (
                            <button
                                type="button"
                                className="bl-row-action bl-row-action--danger"
                                onClick={() => onMarkNonLivre(med)}
                                title="Marquer comme non livré"
                            >
                                Marquer comme non livré
                            </button>
                        )}
                    </td>
                )}
            </tr>
        );
    });

    const renderSectionHeader = (label, tone, count) => (
        <tr className={`bl-section-row bl-section-row--${tone}`}>
            <td colSpan={columnCount}>
                <strong>{label}</strong>
                <span>{count} {plural(count, "ligne")}</span>
            </td>
        </tr>
    );

    return (
        <table className="bl-table">
            <thead>
                <tr>
                    {showInclude && (
                        <th className="bl-th-check" scope="col">
                            <span className="bl-th-check__label">Sur BL</span>
                            <HeaderCheckbox
                                label={includedCount === visible.length ? "Retirer toutes les lignes du BL" : "Inclure toutes les lignes sur le BL"}
                                checked={visible.length > 0 && includedCount === visible.length}
                                indeterminate={includedCount > 0 && includedCount < visible.length}
                                disabled={visible.length === 0 || !onToggleAllIncluded}
                                onChange={checked => onToggleAllIncluded?.(checked)}
                            />
                        </th>
                    )}
                    <th scope="col">Résident</th>
                    <th scope="col">Médicament</th>
                    <th scope="col" className="bl-th-center">Qté</th>
                    <th scope="col">Statut</th>
                    {showDeliverySelection && (
                        <th className="bl-th-check bl-th-check--selection" scope="col">
                            <span className="bl-th-check__label">Sélection</span>
                            <HeaderCheckbox
                                label={selectable.length > 0 && selectedCount === selectable.length ? "Tout désélectionner" : "Tout sélectionner"}
                                checked={selectable.length > 0 && selectedCount === selectable.length}
                                indeterminate={selectedCount > 0 && selectedCount < selectable.length}
                                disabled={selectable.length === 0 || !onToggleAllDelivery}
                                onChange={checked => onToggleAllDelivery?.(checked)}
                            />
                        </th>
                    )}
                    {hasActions && <th scope="col"><span className="bl-visually-hidden">Actions</span></th>}
                </tr>
            </thead>
            <tbody>
                {hasSplit ? (
                    <>
                        {renderSectionHeader("Robot", "robot", robot.length)}
                        {renderRows(robot)}
                        {renderSectionHeader("Étiquetage", "label", etiq.length)}
                        {renderRows(etiq)}
                        {autres.length > 0 && renderRows(autres)}
                    </>
                ) : (
                    renderRows(visible)
                )}
            </tbody>
        </table>
    );
}

/* ─── Toast d'actions sur la sélection ────────────────────────── */
function SelectionToast({ selectedCount, toTraiteCount, toLivreCount, toProductionCount, busy, onTraiter, onLivrer, onRenvoyer, onClear }) {
    return (
        <div className="bl-selection-toast" role="toolbar" aria-label="Actions sur la sélection">
            <output className="bl-selection-toast__count" aria-live="polite">
                {selectedCount} {plural(selectedCount, "produit sélectionné", "produits sélectionnés")}
            </output>
            <div className="bl-selection-toast__buttons">
                <button type="button" className="bl-button" onClick={onTraiter} disabled={busy || toTraiteCount === 0}>
                    Passer en traité ({toTraiteCount})
                </button>
                <button type="button" className="bl-button bl-button--validation" onClick={onLivrer} disabled={busy || toLivreCount === 0}>
                    Marquer livré ({toLivreCount})
                </button>
                <button type="button" className="bl-button bl-button--danger" onClick={onRenvoyer} disabled={busy || toProductionCount === 0}>
                    Renvoyer en production ({toProductionCount})
                </button>
                <button type="button" className="bl-selection-toast__clear" onClick={onClear} disabled={busy}>
                    Désélectionner
                </button>
            </div>
        </div>
    );
}

/* ─── Message de retour (succès, erreur, annulation) ──────────── */
function NoticeBanner({ notice, busy, onUndo, onDismiss }) {
    const isError = notice.tone === "error";
    return (
        <div className={`bl-notice bl-notice--${notice.tone}`} role={isError ? "alert" : "status"}>
            <span className="bl-notice__message">{notice.message}</span>
            {notice.undo && (
                <button type="button" className="bl-button bl-button--ghost" onClick={onUndo} disabled={busy}>
                    Annuler
                </button>
            )}
            <button type="button" className="bl-icon-button" aria-label="Fermer le message" onClick={onDismiss}>×</button>
        </div>
    );
}

/* ─── Confirmation du renvoi en production ─────────────────────── */
function ReturnToProductionModal({ medicaments, busy, onConfirm, onCancel }) {
    const cancelRef = useRef(null);
    useEffect(() => { cancelRef.current?.focus(); }, []);
    const count = medicaments.length;

    return (
        <div className="bl-modal">
            <button type="button" className="bl-modal__backdrop" aria-label="Fermer la confirmation de renvoi" onClick={onCancel} disabled={busy} />
            <dialog
                open
                className="bl-modal__dialog"
                aria-labelledby="return-production-title"
                onKeyDown={event => { if (event.key === "Escape" && !busy) onCancel(); }}
            >
                <h3 id="return-production-title">Renvoyer en production ?</h3>
                <p className="bl-modal__subject">
                    {count} {plural(count, "produit quittera", "produits quitteront")} le bon de livraison et {plural(count, "repartira", "repartiront")} en production.
                </p>
                <ul className="bl-modal__choices">
                    {medicaments.map(med => (
                        <li key={medicamentLineKey(med)}>
                            <strong>{med.nom}{med.dosage ? ` ${med.dosage}` : ""}</strong> — {med.patientNom} {med.patientPrenom}
                        </li>
                    ))}
                </ul>
                <div className="bl-modal__footer">
                    <button ref={cancelRef} type="button" className="bl-button" onClick={onCancel} disabled={busy}>Annuler</button>
                    <button type="button" className="bl-button bl-button--danger" onClick={onConfirm} disabled={busy}>
                        Renvoyer en production
                    </button>
                </div>
            </dialog>
        </div>
    );
}

function DeliveryConfirmModal({ target, pharmacy, deliveryOptions, sending, onConfirm, onCancel, onTargetChange }) {
    const { ehpad, ids = [], cnoIds = [], commentaireLivreur = "", labelDate, labelCode, labelBoxCount = 1, deliveryPlanningId, deliveryOccurrenceDate } = target;
    const deliveryProductCount = ids.length + cnoIds.length;
    const productLabel = deliveryProductCount > 1 ? "produits" : "produit";
    const normalizedBoxCount = Math.max(1, Math.min(40, Number.parseInt(labelBoxCount, 10) || 1));
    const pharmacyReady = hasPharmacyIdentity(pharmacy);
    const selectedDeliveryOptionValue = deliveryPlanningId && deliveryOccurrenceDate
        ? `${deliveryPlanningId}|${deliveryOccurrenceDate}`
        : "";
    const hasDeliveryPlanning = Boolean(deliveryPlanningId && deliveryOccurrenceDate);
    const handleDeliveryOptionChange = (event) => {
        const value = event.target.value;
        if (!value) {
            onTargetChange({ deliveryPlanningId: null, deliveryOccurrenceDate: null });
            return;
        }
        const [planningId, occurrenceDate] = value.split("|");
        onTargetChange({ deliveryPlanningId: Number(planningId), deliveryOccurrenceDate: occurrenceDate });
    };
    const printLabels = () => {
        if (!pharmacyReady) return;
        openLabelPrintWindow({
            ehpad: { ...ehpad, ehpadCode: (labelCode || ehpad.ehpadCode || "").trim().toUpperCase() },
            pharmacy,
            date: labelDate || defaultLabelDate(),
            boxCount: normalizedBoxCount,
        });
    };

    return (
        <div
            className="bl-delivery-modal"
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 1100,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px",
                backdropFilter: "blur(2px)",
            }}
        >
            <button type="button" aria-label="Annuler la confirmation d'envoi" onClick={onCancel} disabled={sending} style={{ position: "absolute", inset: 0, border: 0, background: "rgba(15, 23, 42, 0.45)" }} />
            <dialog
                open
                className="bl-delivery-modal__dialog"
                aria-labelledby="delivery-confirm-title"
                onKeyDown={event => { if (event.key === "Escape" && !sending) onCancel(); }}
                style={{
                    position: "relative",
                    margin: 0,
                    padding: 0,
                    width: "100%",
                    maxWidth: "1040px",
                    maxHeight: "calc(100vh - 40px)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    border: "1px solid #e2e8f0",
                    borderRadius: "14px",
                    background: "white",
                    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)",
                }}
            >
                <div className="bl-delivery-modal__header" style={{ display: "flex", gap: "12px", padding: "20px 22px 14px" }}>
                    <div className="bl-delivery-modal__icon" style={{
                        width: "40px",
                        height: "40px",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "10px",
                        background: ehpad.isStup ? "#f5f3ff" : "#eff6ff",
                        color: ehpad.isStup ? "#6d28d9" : "#2563eb",
                        fontSize: "19px",
                    }}>
                        <TruckIcon size={21} />
                    </div>
                    <div>
                        <h3 id="delivery-confirm-title" style={{ margin: "1px 0 4px", color: "#0f172a", fontSize: "17px" }}>
                            Envoyer en livraison ?
                        </h3>
                        <p style={{ margin: 0, color: "#64748b", fontSize: "13px", lineHeight: 1.5 }}>
                            Le bon sera transmis dans l’espace livreur et ajouté à l’historique.
                        </p>
                    </div>
                </div>

                <div className="bl-delivery-modal__body" style={{ overflowY: "auto", padding: "0 22px 2px" }}>
                    <div className="bl-delivery-modal__grid" style={{ display: "grid", gridTemplateColumns: "minmax(300px, 1fr) 390px", gap: "18px", alignItems: "start" }}>
                        <div className="bl-delivery-modal__main" style={{ display: "grid", gap: "14px", minWidth: 0 }}>
                            <div className="bl-delivery-modal__summary" style={{ padding: "14px 16px", border: "1px solid #e2e8f0", borderRadius: "10px", background: "#f8fafc" }}>
                                <DeliveryModalDetail label="EHPAD" value={ehpad.ehpadNom || ehpad.ehpadCode || "Non renseigné"} />
                                <DeliveryModalDetail label="Tournée" value={ehpad.tourneeName || "Sans tournée"} />
                                <DeliveryModalDetail label="Contenu" value={`${deliveryProductCount} ${productLabel}`} last />
                            </div>

                            <div className="bl-delivery-modal__notice" style={{ padding: "10px 12px", borderRadius: "8px", background: "#eff6ff", color: "#1e40af", fontSize: "12px", lineHeight: 1.45 }}>
                                Les produits sélectionnés passeront au statut « En livraison ».
                            </div>

                            <div style={{ padding: "12px", border: `1px solid ${hasDeliveryPlanning ? "#e2e8f0" : "#C8A44D"}`, borderRadius: "10px", background: hasDeliveryPlanning ? "#ffffff" : "#fffbeb" }}>
                                <label htmlFor="delivery-planning-occurrence" style={{ display: "flex", justifyContent: "space-between", gap: "8px", marginBottom: "6px", color: "#0C3B3C", fontSize: "12px", fontWeight: 800 }}>
                                    <span>Livraison planifiée</span>
                                    <span aria-hidden="true" style={{ color: "#7c5b12", fontSize: "11px" }}>Obligatoire</span>
                                </label>
                                <select
                                    id="delivery-planning-occurrence"
                                    value={selectedDeliveryOptionValue}
                                    onChange={handleDeliveryOptionChange}
                                    disabled={sending}
                                    required
                                    aria-required="true"
                                    aria-invalid={!hasDeliveryPlanning}
                                    style={{ ...inputStyle, width: "100%", minHeight: "38px", borderColor: hasDeliveryPlanning ? "#cbd5e1" : "#C8A44D", boxShadow: hasDeliveryPlanning ? "none" : "0 0 0 3px rgba(200, 164, 77, 0.16)", fontWeight: hasDeliveryPlanning ? 500 : 700 }}
                                >
                                    <option value="">{deliveryOptions.length === 0 ? "Aucune livraison planifiée disponible" : "Sélectionner une livraison planifiée"}</option>
                                    {deliveryOptions.map(option => (
                                        <option key={`${option.planningId}-${option.occurrenceDate}`} value={`${option.planningId}|${option.occurrenceDate}`}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label htmlFor="delivery-driver-comment" style={{ display: "block", marginBottom: "6px", color: "#475569", fontSize: "12px", fontWeight: 700 }}>
                                    Commentaire pour le livreur (facultatif)
                                </label>
                                <textarea
                                    id="delivery-driver-comment"
                                    value={commentaireLivreur}
                                    onChange={event => onTargetChange({ commentaireLivreur: event.target.value })}
                                    disabled={sending}
                                    maxLength={2000}
                                    rows={6}
                                    placeholder="Commentaire"
                                    style={{
                                        width: "100%",
                                        boxSizing: "border-box",
                                        resize: "vertical",
                                        border: "1px solid #cbd5e1",
                                        borderRadius: "9px",
                                        padding: "9px 10px",
                                        color: "#0f172a",
                                        fontSize: "13px",
                                        lineHeight: 1.4,
                                        outline: "none",
                                        background: sending ? "#f8fafc" : "white",
                                    }}
                                />
                            </div>
                        </div>

                        <div className="bl-delivery-modal__labels" style={{ display: "grid", gap: "12px", padding: "12px 14px", border: "1px solid #ccfbf1", borderRadius: "10px", background: "#f0fdfa" }}>
                            <div>
                                <div style={{ color: "#0f766e", fontSize: "12px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                    Étiquettes caisses
                                </div>
                                <div style={{ color: "#64748b", fontSize: "12px", marginTop: "2px" }}>
                                    {normalizedBoxCount} étiquette{normalizedBoxCount > 1 ? "s" : ""} générée{normalizedBoxCount > 1 ? "s" : ""}
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 12px" }}>
                                <label htmlFor="delivery-label-date" style={{ display: "block", color: "#475569", fontSize: "13px", fontWeight: 800 }}>
                                    <span>Date</span>
                                    <input
                                        id="delivery-label-date"
                                        type="date"
                                        value={labelDate || defaultLabelDate()}
                                        onChange={event => onTargetChange({ labelDate: event.target.value })}
                                        disabled={sending}
                                        style={{ ...inputStyle, display: "block", marginTop: "6px", width: "100%" }}
                                    />
                                </label>
                                <label htmlFor="delivery-label-box-count" style={{ display: "block", color: "#475569", fontSize: "13px", fontWeight: 800 }}>
                                    <span>Caisses</span>
                                    <input
                                        id="delivery-label-box-count"
                                        type="number"
                                        min="1"
                                        max="40"
                                        value={labelBoxCount}
                                        onChange={event => onTargetChange({ labelBoxCount: event.target.value })}
                                        disabled={sending}
                                        style={{ ...inputStyle, display: "block", marginTop: "6px", width: "110px", fontWeight: 800 }}
                                    />
                                </label>
                                <label htmlFor="delivery-label-code" style={{ display: "block", gridColumn: "1 / -1", color: "#475569", fontSize: "13px", fontWeight: 800 }}>
                                    <span>Code EHPAD</span>
                                    <input
                                        id="delivery-label-code"
                                        type="text"
                                        maxLength={8}
                                        value={labelCode || ""}
                                        onChange={event => onTargetChange({ labelCode: event.target.value.toUpperCase() })}
                                        disabled={sending}
                                        style={{ ...inputStyle, display: "block", marginTop: "6px", width: "100%", fontWeight: 800 }}
                                    />
                                </label>
                            </div>

                            <div style={{ display: "grid", justifyItems: "center", gap: "10px", padding: "12px", borderRadius: "12px", background: "rgba(255,255,255,0.72)", border: "1px solid #ccfbf1" }}>
                                <LabelPreview
                                    ehpad={ehpad}
                                    pharmacy={pharmacy}
                                    date={labelDate || defaultLabelDate()}
                                    boxCount={normalizedBoxCount}
                                    code={labelCode}
                                    scale={1.12}
                                />
                                <button type="button" onClick={printLabels} disabled={sending || !pharmacyReady} title={!pharmacyReady ? "Chargement des informations de la pharmacie" : undefined} style={{ width: "246px", padding: "10px 14px", borderRadius: "8px", border: "1px solid #14b8a6", background: "#ccfbf1", cursor: sending || !pharmacyReady ? "not-allowed" : "pointer", fontSize: "14px", color: "#0f766e", fontWeight: 800, opacity: sending || !pharmacyReady ? 0.55 : 1 }}>
                                    {pharmacyReady ? "Imprimer" : "Pharmacie en cours de chargement…"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bl-delivery-modal__footer" style={{ display: "flex", justifyContent: "flex-end", gap: "10px", padding: "18px 22px 20px", flexShrink: 0 }}>
                    <button type="button" onClick={onCancel} disabled={sending} style={{ ...btnSecondary, padding: "8px 14px", opacity: sending ? 0.55 : 1 }}>
                        Annuler
                    </button>
                    <button type="button" onClick={onConfirm} disabled={sending || !hasDeliveryPlanning} title={!hasDeliveryPlanning ? "Sélectionnez une livraison planifiée" : undefined} style={{ ...btnPrimary, padding: "8px 14px", background: "#2563eb", borderColor: "#2563eb", color: "white", opacity: sending || !hasDeliveryPlanning ? 0.55 : 1, cursor: sending || !hasDeliveryPlanning ? "not-allowed" : "pointer" }}>
                        {sending ? "Envoi…" : "Confirmer l’envoi"}
                    </button>
                </div>
            </dialog>
        </div>
    );
}

function DeliveryModalDetail({ label, value, last = false }) {
    return (
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", padding: "6px 0", borderBottom: last ? "none" : "1px solid #e2e8f0" }}>
            <span style={{ color: "#94a3b8", fontSize: "12px", fontWeight: 600 }}>{label}</span>
            <span style={{ color: "#334155", fontSize: "12px", fontWeight: 700, textAlign: "right" }}>{value}</span>
        </div>
    );
}

/* ─── Barre de filtres (bons en cours) ────────────────────────── */
function FilterBarEnCours({
    availableTournees, selectedTourneeId, setSelectedTourneeId,
    availableEhpads, selectedEhpadId, setSelectedEhpadId,
    selectedStatuts, setSelectedStatuts,
    showScopeFilters,
    searchQuery, setSearchQuery, searchInputRef, searchResultCount,
    hasActiveFilters, onReset, onRefresh, loading,
    printTools = null,
}) {
    const toggle = key =>
        setSelectedStatuts(prev =>
            prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]
        );

    return (
        <div className="bl-filters" role="search" aria-label="Recherche et filtres des bons">
            <div className="bl-filters__search">
                <span aria-hidden="true" className="bl-filters__search-icon">⌕</span>
                <input
                    ref={searchInputRef}
                    type="search"
                    aria-label="Rechercher un patient ou un produit"
                    placeholder="Rechercher un patient ou un produit…"
                    value={searchQuery}
                    onChange={event => setSearchQuery(event.target.value)}
                />
                <kbd>Ctrl/⌘ F</kbd>
                {searchQuery && (
                    <output className="bl-filters__count">
                        {searchResultCount} {plural(searchResultCount, "résultat")}
                    </output>
                )}
            </div>

            {showScopeFilters && (
                <>
                    <label className="bl-filters__field" htmlFor="bl-filter-tournee">
                        <span>Tournée</span>
                        <select
                            id="bl-filter-tournee"
                            value={selectedTourneeId || ""}
                            onChange={e => setSelectedTourneeId(e.target.value ? Number(e.target.value) : null)}
                        >
                            <option value="">Toutes</option>
                            {availableTournees.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </label>

                    <label className="bl-filters__field" htmlFor="bl-filter-ehpad">
                        <span>EHPAD</span>
                        <select
                            id="bl-filter-ehpad"
                            value={selectedEhpadId || ""}
                            onChange={e => setSelectedEhpadId(parseSelectIdentifier(e.target.value))}
                        >
                            <option value="">Tous</option>
                            {availableEhpads.map(e => <option key={e.id} value={e.id}>{e.nom}</option>)}
                        </select>
                    </label>
                </>
            )}

            {!showScopeFilters && (
                <fieldset className="bl-filters__statuses">
                    <legend className="bl-visually-hidden">Statuts affichés</legend>
                    {STATUTS_BON.map(({ key, label }) => {
                        const active = selectedStatuts.includes(key);
                        const col = StatutMedicamentColors[key] || { bg: "#f1f5f9", color: "#64748b" };
                        return (
                            <button
                                key={key}
                                type="button"
                                aria-pressed={active}
                                onClick={() => toggle(key)}
                                className={`bl-chip${active ? " is-active" : ""}`}
                                style={active ? { borderColor: col.color, background: col.bg, color: col.color } : undefined}
                            >
                                {label}
                            </button>
                        );
                    })}
                </fieldset>
            )}

            <div className="bl-filters__tools">
                {hasActiveFilters && (
                    <button type="button" className="bl-link-button" onClick={onReset}>Réinitialiser</button>
                )}
                {printTools}
                <button type="button" className="bl-icon-button bl-icon-button--bordered" onClick={onRefresh} title="Actualiser" aria-label="Actualiser" disabled={loading}>↺</button>
            </div>
        </div>
    );
}

/* ─── Onglet bons en cours ─────────────────────────────────────── */
function BonsEnCoursTab({ pharmacy = null }) {
    const { user } = useAuth() || {};
    const [ehpadBons, setEhpadBons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedStatuts, setSelectedStatuts] = useState(DEFAULT_STATUTS);
    const [selectedTourneeId, setSelectedTourneeId] = useState(null);
    const [selectedEhpadId, setSelectedEhpadId] = useState(null);
    const [openedBon, setOpenedBon] = useState(null);
    const [returnTarget, setReturnTarget] = useState(null);
    const [deliveryTarget, setDeliveryTarget] = useState(null);
    const [deliverySending, setDeliverySending] = useState(false);
    const [deliveryPlannings, setDeliveryPlannings] = useState([]);
    const [includeById, setIncludeById] = useState({});
    const [selectedDeliveryIds, setSelectedDeliveryIds] = useState(() => new Set());
    const [printGroupingMode, setPrintGroupingMode] = useState("line");
    const [previewOpen, setPreviewOpen] = useState(false);
    const [notice, setNotice] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const searchInputRef = useRef(null);

    const refresh = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        selectedStatuts.forEach(s => params.append("statuts", s));

        Promise.all([
            API.get(`/bon-livraison?${params}`, { noRedirectOn401: true }),
            API.get("/cno/patients/bon-livraison", { noRedirectOn401: true }).catch(() => ({ data: [] })),
        ])
            .then(([r, cnoResponse]) => setEhpadBons([...(r.data || []), ...cnoBonFromLines(cnoResponse.data)]))
            .catch(error => {
                console.error(error);
                setNotice({ tone: "error", message: "Impossible de charger les bons en cours. Vérifiez la connexion puis actualisez." });
            })
            .finally(() => setLoading(false));
    }, [selectedStatuts]);

    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        API.get("/admin/delivery-plannings", { noRedirectOn401: true })
            .then(r => setDeliveryPlannings(Array.isArray(r.data) ? r.data : []))
            .catch(() => setDeliveryPlannings([]));
    }, []);

    const availableTournees = useMemo(() => {
        const map = new Map();
        ehpadBons.forEach(bon => {
            if (bon.tourneeId != null && !map.has(bon.tourneeId)) {
                map.set(bon.tourneeId, bon.tourneeName || String(bon.tourneeId));
            }
        });
        return [...map.entries()]
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [ehpadBons]);

    const cardBons = useMemo(() => ehpadBons.flatMap(bon => {
        if (selectedTourneeId != null && bon.tourneeId !== selectedTourneeId) return [];
        if (selectedEhpadId != null && bon.ehpadId !== selectedEhpadId) return [];
        const medicaments = bon.medicaments.filter(m => matchesDeliverySearch(m, searchQuery));
        return medicaments.length > 0 ? [{ ...bon, medicaments }] : [];
    }), [ehpadBons, searchQuery, selectedTourneeId, selectedEhpadId]);

    // Un bon ouvert se scinde en bon standard et bon stupéfiants ; la recherche ne filtre que ses lignes.
    const openedVariants = useMemo(() => {
        if (!openedBon) return [];
        const bon = ehpadBons.find(item => item.ehpadId === openedBon.ehpadId);
        return bon ? splitBonByClassification(bon) : [];
    }, [ehpadBons, openedBon]);
    const openedVariant = openedVariants.find(bon => bon.isStup === openedBon?.isStup) ?? openedVariants[0] ?? null;
    const currentEhpad = useMemo(
        () => openedVariant
            ? { ...openedVariant, medicaments: openedVariant.medicaments.filter(m => matchesDeliverySearch(m, searchQuery)) }
            : null,
        [openedVariant, searchQuery]
    );
    const viewMode = currentEhpad ? "detail" : "cards";

    const searchResultCount = currentEhpad
        ? currentEhpad.medicaments.length
        : cardBons.reduce((totalCount, bon) => totalCount + bon.medicaments.length, 0);

    useEffect(() => {
        if (!loading && openedBon && openedVariants.length === 0) setOpenedBon(null);
    }, [loading, openedBon, openedVariants.length]);

    const availableEhpadsInFilter = useMemo(() => {
        const seen = new Set();
        return ehpadBons
            .filter(b => selectedTourneeId == null || b.tourneeId === selectedTourneeId)
            .filter(b => {
                if (seen.has(b.ehpadId)) {
                    return false;
                }
                seen.add(b.ehpadId);
                return true;
            })
            .map(b => ({ id: b.ehpadId, nom: b.ehpadNom }))
            .sort((a, b) => a.nom.localeCompare(b.nom));
    }, [ehpadBons, selectedTourneeId]);

    // Tournée et EHPAD ne servent qu'à trier les cartes : ils sont masqués dans le bon ouvert.
    // Cartes : tournée et EHPAD ; bon ouvert : statuts des lignes. Chaque vue n'affiche que ses filtres.
    const showScopeFilters = viewMode === "cards";
    const hasActiveFilters = Boolean(
        searchQuery
        || (showScopeFilters
            ? selectedTourneeId != null || selectedEhpadId != null
            : !sameStatuts(selectedStatuts, DEFAULT_STATUTS))
    );

    const handleTourneeChange = (v) => {
        setSelectedTourneeId(v);
        setSelectedEhpadId(null);
    };

    const resetStatuts = () => {
        if (!sameStatuts(selectedStatuts, DEFAULT_STATUTS)) setSelectedStatuts(DEFAULT_STATUTS);
    };

    const resetFilters = () => {
        setSearchQuery("");
        if (showScopeFilters) {
            setSelectedTourneeId(null);
            setSelectedEhpadId(null);
        } else {
            resetStatuts();
        }
    };

    const openBon = (ehpadId) => {
        setSearchQuery("");
        setOpenedBon({ ehpadId, isStup: false });
    };

    const backToList = () => {
        // Les statuts ne sont réglables que dans le bon : on ne garde pas un filtre invisible sur les cartes.
        resetStatuts();
        setOpenedBon(null);
        setSelectedDeliveryIds(new Set());
    };
    const includedCurrentMedicaments = useMemo(
        () => currentEhpad?.medicaments.filter(m => includeById[medicamentLineKey(m)] !== false) || [],
        [currentEhpad, includeById]
    );
    const printableEhpad = useMemo(
        () => currentEhpad
            ? { ...currentEhpad, medicaments: includedCurrentMedicaments }
            : null,
        [currentEhpad, includedCurrentMedicaments]
    );
    const deliverySelectableMedicaments = useMemo(
        () => currentEhpad?.medicaments.filter(isDeliverySelectableLine) || [],
        [currentEhpad]
    );
    const selectedDeliveryMedicaments = deliverySelectableMedicaments.filter(
        m => selectedDeliveryIds.has(medicamentLineKey(m))
    );
    const selectedEnCommandeMedicaments = selectedDeliveryMedicaments.filter(m => m.statut === "EN_COMMANDE");
    const selectedDirectDeliveryMedicaments = selectedDeliveryMedicaments.filter(isDirectlyDeliverableLine);
    const selectedReturnableMedicaments = selectedDeliveryMedicaments.filter(isReturnableToProductionLine);
    const sendableCount = includedCurrentMedicaments.filter(m => m.statut === "TRAITE").length;
    const pharmacyReady = hasPharmacyIdentity(pharmacy);

    useEffect(() => {
        if (!currentEhpad) {
            setSelectedDeliveryIds(new Set());
            return;
        }
        const currentIds = new Set(currentEhpad.medicaments.map(medicamentLineKey));
        setSelectedDeliveryIds(prev => new Set([...prev].filter(id => currentIds.has(id))));
    }, [currentEhpad]);

    const toggleInclude = (id, checked) => {
        setIncludeById(prev => ({ ...prev, [id]: checked }));
    };

    const setAllCurrentIncluded = (checked) => {
        if (!currentEhpad) return;
        setIncludeById(prev => {
            const next = { ...prev };
            currentEhpad.medicaments.forEach(med => {
                next[medicamentLineKey(med)] = checked;
            });
            return next;
        });
    };

    const toggleDeliverySelection = (id, checked) => {
        setSelectedDeliveryIds(prev => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
    };

    const setAllCurrentDeliverySelected = (checked) => {
        const ids = deliverySelectableMedicaments.map(medicamentLineKey);
        setSelectedDeliveryIds(prev => {
            const next = new Set(prev);
            ids.forEach(id => {
                if (checked) next.add(id);
                else next.delete(id);
            });
            return next;
        });
    };

    const applyBatchPatch = async (items, patchBuilder) => {
        if (items.length === 0) return;
        await API.patch(
            "/medicaments/batch",
            items.map(item => ({ id: item.id, patch: patchBuilder(item) })),
            { noRedirectOn401: true }
        );
        refresh();
    };

    const patchCnoLines = async (items, patchBuilder) => {
        if (items.length === 0) return;
        await Promise.all(items.map(item => API.patch(
            `/cno/patients/delivery-lines/${item.id}`,
            patchBuilder(item),
            { noRedirectOn401: true }
        )));
        refresh();
    };

    const handleTraiterCommandes = async () => {
        if (selectedEnCommandeMedicaments.length === 0 || bulkBusy) return;
        setBulkBusy(true);
        const username = user?.username || user?.email || null;
        const items = selectedEnCommandeMedicaments;
        try {
            await applyBatchPatch(items, () => makePatch({
                statut: "TRAITE",
                productionValidated: true,
                productionValidatedUsername: username,
            }));
            setNotice({
                tone: "info",
                message: `${items.length} ${plural(items.length, "produit passé", "produits passés")} en traité.`,
                undo: {
                    items,
                    patchBuilder: () => makePatch({
                        statut: "EN_COMMANDE",
                        productionValidated: false,
                    }),
                },
            });
        } catch (e) {
            console.error("Erreur lors du passage en traité :", e);
            setNotice({ tone: "error", message: "Le passage en traité a échoué. Aucun produit n'a été modifié, réessayez." });
        } finally {
            setBulkBusy(false);
        }
    };

    const handleMarquerSelectionLivree = async () => {
        if (selectedDirectDeliveryMedicaments.length === 0 || bulkBusy) return;
        setBulkBusy(true);
        const items = selectedDirectDeliveryMedicaments;
        try {
            const partialItems = items.filter(isPartialDeliverableLine);
            const cnoItems = items.filter(isCnoLine);
            const patchItems = items.filter(item => !isPartialDeliverableLine(item) && !isCnoLine(item));

            if (patchItems.length > 0) {
                await applyBatchPatch(patchItems, () => makePatch({ statut: "LIVRE" }));
            }
            if (cnoItems.length > 0) {
                await patchCnoLines(cnoItems, () => ({ statut: "LIVRE" }));
            }
            if (partialItems.length > 0) {
                await API.post(
                    "/bon-livraison/livrer",
                    { ids: [...new Set(partialItems.map(item => item.id))] },
                    { noRedirectOn401: true }
                );
                refresh();
            }
            setSelectedDeliveryIds(new Set());
            const message = `${items.length} ${plural(items.length, "produit marqué livré", "produits marqués livrés")}.`;
            setNotice(partialItems.length > 0
                ? { tone: "success", message }
                : {
                    tone: "info",
                    message,
                    undo: { items, patchBuilder: item => makePatch({ statut: item.statut || "" }) },
                });
        } catch (e) {
            console.error("Erreur lors du passage en livré :", e);
            setNotice({ tone: "error", message: "Le passage en livré a échoué. Actualisez la liste pour vérifier l'état des produits." });
        } finally {
            setBulkBusy(false);
        }
    };

    const handleUndo = async () => {
        if (!notice?.undo || bulkBusy) return;
        setBulkBusy(true);
        try {
            const cnoItems = notice.undo.items.filter(isCnoLine);
            const medicationItems = notice.undo.items.filter(item => !isCnoLine(item));
            await applyBatchPatch(medicationItems, notice.undo.patchBuilder);
            await patchCnoLines(cnoItems, notice.undo.patchBuilder);
            setNotice({ tone: "success", message: "Modification annulée." });
        } catch (e) {
            console.error("Erreur lors de l'annulation :", e);
            setNotice({ tone: "error", message: "L'annulation a échoué. Actualisez la liste pour vérifier l'état des produits." });
        } finally {
            setBulkBusy(false);
        }
    };

    const handleRenvoyerEnProduction = async () => {
        if (!returnTarget || bulkBusy) return;
        setBulkBusy(true);
        const items = returnTarget;
        try {
            await Promise.all(items.map(item => API.post(
                `/bon-livraison/medicament/${item.id}/retirer`,
                {},
                { noRedirectOn401: true }
            )));
            setNotice({
                tone: "success",
                message: `${items.length} ${plural(items.length, "produit renvoyé", "produits renvoyés")} en production.`,
            });
            setReturnTarget(null);
            setSelectedDeliveryIds(new Set());
        } catch (e) {
            console.error("Erreur lors du renvoi en production :", e);
            setNotice({ tone: "error", message: "Le renvoi en production a échoué. Actualisez la liste pour vérifier l'état des produits." });
            setReturnTarget(null);
        } finally {
            setBulkBusy(false);
            refresh();
        }
    };

    const handleEnvoyerEnLivraison = useCallback(() => {
        if (!currentEhpad) return;
        const items = includedCurrentMedicaments.filter(m => m.statut === "TRAITE");
        const ids = [...new Set(items.filter(m => !isCnoLine(m)).map(m => m.id))];
        const cnoIds = [...new Set(items.filter(isCnoLine).map(m => m.id))];
        if (ids.length === 0 && cnoIds.length === 0) return;
        const options = buildDeliveryOccurrenceOptions(deliveryPlannings, printableEhpad?.ehpadId);
        setDeliveryTarget({
            ehpad: printableEhpad,
            ids,
            cnoIds,
            commentaireLivreur: "",
            labelDate: defaultLabelDate(),
            labelCode: (printableEhpad?.ehpadCode || "").toUpperCase(),
            labelBoxCount: 1,
            deliveryOptions: options,
            deliveryPlanningId: null,
            deliveryOccurrenceDate: null,
        });
    }, [currentEhpad, deliveryPlannings, includedCurrentMedicaments, printableEhpad]);

    const confirmerEnLivraison = useCallback(async () => {
        if (!deliveryTarget?.deliveryPlanningId || !deliveryTarget?.deliveryOccurrenceDate || deliverySending) return;
        setDeliverySending(true);
        try {
            const requests = [];
            if ((deliveryTarget.ids || []).length > 0) {
                requests.push(API.post(
                    "/bon-livraison/en-livraison",
                    {
                        ids: deliveryTarget.ids,
                        commentaireLivreur: deliveryTarget.commentaireLivreur?.trim() || null,
                        nombreCaisses: Math.max(1, Math.min(40, Number.parseInt(deliveryTarget.labelBoxCount, 10) || 1)),
                        deliveryPlanningId: deliveryTarget.deliveryPlanningId || null,
                        deliveryOccurrenceDate: deliveryTarget.deliveryOccurrenceDate || null,
                    },
                    { noRedirectOn401: true }
                ));
            }
            (deliveryTarget.cnoIds || []).forEach(id => {
                requests.push(API.patch(
                    `/cno/patients/delivery-lines/${id}`,
                    { statut: "EN_LIVRAISON" },
                    { noRedirectOn401: true }
                ));
            });
            await Promise.all(requests);
            const sentCount = (deliveryTarget.ids || []).length + (deliveryTarget.cnoIds || []).length;
            setNotice({
                tone: "success",
                message: `Bon ${deliveryTarget.ehpad.ehpadNom} envoyé en livraison (${sentCount} ${plural(sentCount, "produit")}).`,
            });
            setDeliveryTarget(null);
            setOpenedBon(null);
            refresh();
        } catch (e) {
            console.error("Erreur lors de l'envoi en livraison :", e);
            setNotice({ tone: "error", message: "L'envoi en livraison a échoué. Le bon n'a pas été transmis, réessayez." });
        } finally {
            setDeliverySending(false);
        }
    }, [deliverySending, deliveryTarget, refresh]);

    const handlePrint = () => {
        if (!printableEhpad || !pharmacyReady) return;
        openPrintWindow(printableEhpad, null, pharmacy, printableEhpad.isStup, "", printGroupingMode);
    };

    useEffect(() => {
        const onKey = (e) => {
            const modifier = e.metaKey || e.ctrlKey;
            if (modifier && e.key.toLowerCase() === "f" && !deliveryTarget && !returnTarget) {
                e.preventDefault();
                searchInputRef.current?.focus();
                searchInputRef.current?.select();
                return;
            }
            if (e.key === "Escape" && document.activeElement === searchInputRef.current && searchQuery) {
                e.preventDefault();
                setSearchQuery("");
                return;
            }
            if (!modifier || e.key !== "Enter" || returnTarget) return;
            e.preventDefault();
            if (deliveryTarget) confirmerEnLivraison();
            else if (currentEhpad) handleEnvoyerEnLivraison();
        };
        globalThis.addEventListener("keydown", onKey);
        return () => globalThis.removeEventListener("keydown", onKey);
    }, [confirmerEnLivraison, currentEhpad, deliveryTarget, handleEnvoyerEnLivraison, returnTarget, searchQuery]);

    const showDetail = Boolean(currentEhpad);
    const sendDisabledReason = sendableCount === 0
        ? "Aucun produit « Traité » n'est inclus sur le BL"
        : null;

    return (
        <div className="bl-tab">
            {showDetail && (
                <EhpadPanelHeader
                    ehpad={currentEhpad}
                    hasStupVariant={openedVariants.length > 1}
                    onBack={backToList}
                    onSelectVariant={isStup => setOpenedBon(current => ({ ...current, isStup }))}
                />
            )}
            <FilterBarEnCours
                availableTournees={availableTournees}
                selectedTourneeId={selectedTourneeId}
                setSelectedTourneeId={handleTourneeChange}
                availableEhpads={availableEhpadsInFilter}
                selectedEhpadId={selectedEhpadId}
                setSelectedEhpadId={setSelectedEhpadId}
                selectedStatuts={selectedStatuts}
                setSelectedStatuts={setSelectedStatuts}
                showScopeFilters={showScopeFilters}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                searchInputRef={searchInputRef}
                searchResultCount={searchResultCount}
                hasActiveFilters={hasActiveFilters}
                onReset={resetFilters}
                onRefresh={refresh}
                loading={loading}
                printTools={showDetail && (
                    <fieldset className="bl-filters__print">
                        <legend className="bl-visually-hidden">Bon imprimé</legend>
                        <label htmlFor="print-grouping-mode" className="bl-filters__field">
                            <span>Regroupement</span>
                            <select
                                id="print-grouping-mode"
                                value={printGroupingMode}
                                onChange={event => setPrintGroupingMode(event.target.value)}
                            >
                                {PRINT_GROUPING_MODES.map(mode => (
                                    <option key={mode.key} value={mode.key}>{mode.label}</option>
                                ))}
                            </select>
                        </label>
                        <button
                            type="button"
                            className={`bl-button${previewOpen ? " is-active" : ""}`}
                            aria-pressed={previewOpen}
                            onClick={() => setPreviewOpen(open => !open)}
                        >
                            Aperçu
                        </button>
                    </fieldset>
                )}
            />

            {notice && !showDetail && (
                <div className="bl-notice-slot">
                    <NoticeBanner notice={notice} busy={bulkBusy} onUndo={handleUndo} onDismiss={() => setNotice(null)} />
                </div>
            )}

            <div className="bl-workspace">
                {loading && !showDetail && (
                    <div className="bl-empty">Chargement…</div>
                )}

                {!loading && !showDetail && cardBons.length === 0 && (
                    <div className="bl-empty">
                        <p>
                            {searchQuery
                                ? `Aucun patient ou produit ne correspond à « ${searchQuery} ».`
                                : "Aucun médicament à livrer pour les filtres sélectionnés."}
                        </p>
                        {hasActiveFilters && (
                            <button type="button" className="bl-button" onClick={resetFilters}>Réinitialiser les filtres</button>
                        )}
                    </div>
                )}

                {!loading && !showDetail && cardBons.length > 0 && (
                    <div className="bl-card-grid" aria-label="Bons de livraison par EHPAD">
                        {cardBons.map((bon) => (
                            <BonEnCoursCard
                                key={bon.ehpadId}
                                ehpad={bon}
                                onOpen={() => openBon(bon.ehpadId)}
                            />
                        ))}
                    </div>
                )}

                {showDetail && (
                    <>
                        <div className="bl-detail-main">
                            <div className="bl-detail-main__table">
                                {currentEhpad.medicaments.length === 0 && (
                                    <div className="bl-empty">
                                        <p>Aucune ligne de ce bon ne correspond à « {searchQuery} ».</p>
                                        <button type="button" className="bl-button" onClick={resetFilters}>Réinitialiser les filtres</button>
                                    </div>
                                )}
                                <MedicamentTable
                                    medicaments={currentEhpad.medicaments}
                                    includeById={includeById}
                                    onToggleInclude={toggleInclude}
                                    onToggleAllIncluded={setAllCurrentIncluded}
                                    selectedDeliveryIds={selectedDeliveryIds}
                                    onToggleDelivery={toggleDeliverySelection}
                                    onToggleAllDelivery={setAllCurrentDeliverySelected}
                                />
                            </div>
                            {selectedDeliveryMedicaments.length > 0 && (
                                <SelectionToast
                                    selectedCount={selectedDeliveryMedicaments.length}
                                    toTraiteCount={selectedEnCommandeMedicaments.length}
                                    toLivreCount={selectedDirectDeliveryMedicaments.length}
                                    toProductionCount={selectedReturnableMedicaments.length}
                                    busy={bulkBusy}
                                    onTraiter={handleTraiterCommandes}
                                    onLivrer={handleMarquerSelectionLivree}
                                    onRenvoyer={() => setReturnTarget(selectedReturnableMedicaments)}
                                    onClear={() => setAllCurrentDeliverySelected(false)}
                                />
                            )}
                        </div>

                        {previewOpen && printableEhpad && (
                            <PrintPreviewPanel
                                ehpad={printableEhpad}
                                pharmacy={pharmacy}
                                groupingMode={printGroupingMode}
                                onClose={() => setPreviewOpen(false)}
                            />
                        )}
                    </>
                )}
            </div>

            {showDetail && (
                <footer className="bl-actions">
                    {notice && (
                        <NoticeBanner notice={notice} busy={bulkBusy} onUndo={handleUndo} onDismiss={() => setNotice(null)} />
                    )}
                    <div className="bl-actions__row">
                        <span className="bl-actions__count">
                            {includedCurrentMedicaments.length}/{currentEhpad.medicaments.length} {plural(currentEhpad.medicaments.length, "ligne")} sur le BL
                        </span>

                        <fieldset className="bl-actions__send">
                            <legend className="bl-visually-hidden">Imprimer et envoyer le bon</legend>
                            {sendDisabledReason && <span className="bl-actions__hint">{sendDisabledReason}</span>}
                            <button
                                type="button"
                                className="bl-print-button"
                                onClick={handlePrint}
                                disabled={includedCurrentMedicaments.length === 0 || !pharmacyReady}
                                title={pharmacyReady ? undefined : "Chargement des informations de la pharmacie"}
                            >
                                <span aria-hidden="true">⎙</span>
                                <span>{pharmacyReady ? "Imprimer le BL" : "Pharmacie en cours de chargement…"}</span>
                            </button>
                            <button
                                type="button"
                                className={`bl-send-button${currentEhpad.isStup ? " bl-send-button--stup" : ""}`}
                                onClick={handleEnvoyerEnLivraison}
                                disabled={sendableCount === 0}
                            >
                                <TruckIcon size={18} />
                                <span>Envoyer en livraison ({sendableCount})</span>
                                <kbd>Ctrl/⌘ ↵</kbd>
                            </button>
                        </fieldset>
                    </div>
                </footer>
            )}

            {returnTarget && (
                <ReturnToProductionModal
                    medicaments={returnTarget}
                    busy={bulkBusy}
                    onConfirm={handleRenvoyerEnProduction}
                    onCancel={() => setReturnTarget(null)}
                />
            )}
            {deliveryTarget && (
                <DeliveryConfirmModal
                    target={deliveryTarget}
                    pharmacy={pharmacy}
                    deliveryOptions={deliveryTarget.deliveryOptions || []}
                    sending={deliverySending}
                    onConfirm={confirmerEnLivraison}
                    onCancel={() => setDeliveryTarget(null)}
                    onTargetChange={patch => setDeliveryTarget(current => (
                        current ? { ...current, ...patch } : current
                    ))}
                />
            )}
        </div>
    );
}


/* ─── Carte bon historique ─────────────────────────────────────── */
function BonHistoriqueCard({ bon, pharmacy = null }) {
    const [expanded, setExpanded] = useState(false);
    const [medicaments, setMedicaments] = useState(bon.medicaments);

    const totalUnites = medicaments.reduce((acc, m) => acc + (m.quantite || 0), 0);
    const dateStr = new Date(bon.dateLivraison).toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
    const isStup = medicaments.length > 0 && medicaments.every(m => m.classificationMedicament === "STUP");
    const borderColor = isStup ? "#6d28d9" : "#e2e8f0";
    const headerBg    = isStup ? "#f5f3ff" : "#f8fafc";
    const pharmacyReady = hasPharmacyIdentity(pharmacy);

    const handleMarkNonLivre = async (med) => {
        setMedicaments(prev => prev.map(m => m.id === med.id ? { ...m, statut: "NON_LIVRE" } : m));
        try {
            await API.patch(`/bon-livraison/medicament/${med.id}/statut`, { statut: "NON_LIVRE" }, { noRedirectOn401: true });
        } catch {
            setMedicaments(prev => prev.map(m => m.id === med.id ? { ...m, statut: med.statut } : m));
        }
    };

    return (
        <div style={{ marginBottom: "10px", border: `1px solid ${borderColor}`, borderRadius: "10px", overflow: "hidden", background: "white" }}>
            <div
                style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 20px", background: headerBg, borderBottom: expanded ? `1px solid ${borderColor}` : "none", cursor: "pointer", userSelect: "none" }}
            >
                <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => setExpanded(v => !v)}
                    style={{ flex: 1, border: 0, padding: 0, background: "transparent", color: "inherit", font: "inherit", textAlign: "left", cursor: "pointer" }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ fontWeight: 600, fontSize: "14px", color: "#1e293b" }}>{dateStr}</span>
                        {isStup && (
                            <span style={{ padding: "2px 8px", borderRadius: "999px", background: "#ede9fe", color: "#6d28d9", fontSize: "11px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                                Stupéfiants
                            </span>
                        )}
                    </div>
                    <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>
                        {medicaments.length} ligne{medicaments.length > 1 ? "s" : ""}
                        <span>&nbsp;·&nbsp;</span>
                        <strong>{totalUnites} unité{totalUnites > 1 ? "s" : ""}</strong>
                        {bon.nombreCaisses != null && (
                            <>
                                <span>&nbsp;·&nbsp;</span>
                                <strong>{bon.nombreCaisses} caisse{bon.nombreCaisses > 1 ? "s" : ""}</strong>
                            </>
                        )}
                    </div>
                    {bon.commentaireLivreur && (
                        <div style={{
                            marginTop: "8px",
                            padding: "7px 0 7px 10px",
                            borderLeft: "3px solid #f59e0b",
                            color: "#475569",
                            fontSize: "12px",
                            lineHeight: 1.4,
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere",
                        }}>
                            <span style={{ display: "block", marginBottom: "2px", color: "#b45309", fontSize: "10px", fontWeight: 800, textTransform: "uppercase" }}>
                                Commentaire de la pharmacie
                            </span>
                            {bon.commentaireLivreur}
                        </div>
                    )}
                </button>
                <button
                    type="button"
                    onClick={e => { e.stopPropagation(); if (pharmacyReady) openPrintWindow(bon, bon.dateLivraison, pharmacy, isStup); }}
                    disabled={!pharmacyReady}
                    title={!pharmacyReady ? "Chargement des informations de la pharmacie" : undefined}
                    style={{ ...btnSecondary, opacity: pharmacyReady ? 1 : 0.5, cursor: pharmacyReady ? "pointer" : "not-allowed" }}
                >
                    Réimprimer
                </button>
                <button
                    type="button"
                    aria-label={expanded ? "Réduire le bon" : "Développer le bon"}
                    aria-expanded={expanded}
                    onClick={() => setExpanded(v => !v)}
                    style={{ border: 0, padding: 0, background: "transparent", color: "#94a3b8", fontSize: "14px", minWidth: "16px", textAlign: "center", cursor: "pointer" }}
                >
                    {expanded ? "▲" : "▼"}
                </button>
            </div>

            {expanded && <MedicamentTable medicaments={medicaments} onMarkNonLivre={handleMarkNonLivre} />}
        </div>
    );
}

/* ─── Onglet historique ────────────────────────────────────────── */
function HistoriqueTab({ pharmacy = null }) {
    const location = useLocation();
    const initialSearchParams = new URLSearchParams(location.search || "");
    const initialEhpadId = Number(initialSearchParams.get("ehpadId"));
    const [historique, setHistorique] = useState([]);
    const [loading, setLoading] = useState(false);
    const [historiquePage, setHistoriquePage] = useState(0);
    const [totalHistoriqueElements, setTotalHistoriqueElements] = useState(0);
    const [totalHistoriquePages, setTotalHistoriquePages] = useState(0);
    const [ehpads, setEhpads] = useState([]);
    const [tournees, setTournees] = useState([]);
    const [selectedEhpadId, setSelectedEhpadId] = useState(
        Number.isFinite(initialEhpadId) && initialEhpadId > 0 ? initialEhpadId : null
    );
    const [selectedTourneeId, setSelectedTourneeId] = useState(null);
    const [selectedDateDebut, setSelectedDateDebut] = useState(initialSearchParams.get("dateDebut") || null);
    const [selectedDateFin, setSelectedDateFin] = useState(initialSearchParams.get("dateFin") || null);

    useEffect(() => {
        const params = new URLSearchParams(location.search || "");
        const ehpadId = Number(params.get("ehpadId"));
        setSelectedEhpadId(Number.isFinite(ehpadId) && ehpadId > 0 ? ehpadId : null);
        setSelectedDateDebut(params.get("dateDebut") || null);
        setSelectedDateFin(params.get("dateFin") || null);
        setHistoriquePage(0);
    }, [location.search]);

    useEffect(() => {
        API.get("/ehpads", { noRedirectOn401: true })
            .then(r => setEhpads(r.data || []))
            .catch(() => setEhpads([]));
        API.get("/ehpads/tournees", { noRedirectOn401: true })
            .then(r => setTournees(r.data || []))
            .catch(() => setTournees([]));
    }, []);

    const refresh = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (selectedEhpadId) params.append("ehpadId", String(selectedEhpadId));
        if (selectedTourneeId) params.append("tourneeId", String(selectedTourneeId));
        if (selectedDateDebut) params.append("dateDebut", selectedDateDebut);
        if (selectedDateFin) params.append("dateFin", selectedDateFin);
        params.append("page", String(historiquePage));
        params.append("size", String(HISTORIQUE_PAGE_SIZE));
        params.append("includeMedicaments", "true");

        API.get(`/bon-livraison/historique/page?${params}`, { noRedirectOn401: true })
            .then(r => {
                const data = r.data || {};
                setHistorique(data.content || []);
                setTotalHistoriqueElements(data.totalElements ?? 0);
                setTotalHistoriquePages(data.totalPages ?? 0);
                if (typeof data.number === "number" && data.number !== historiquePage) {
                    setHistoriquePage(data.number);
                }
            })
            .catch(error => {
                console.error(error);
                setHistorique([]);
                setTotalHistoriqueElements(0);
                setTotalHistoriquePages(0);
            })
            .finally(() => setLoading(false));
    }, [selectedEhpadId, selectedTourneeId, selectedDateDebut, selectedDateFin, historiquePage]);

    useEffect(() => { refresh(); }, [refresh]);

    const availableEhpadsInFilter = useMemo(() => {
        const selectedTournee = tournees.find(t => Number(t.id) === Number(selectedTourneeId));
        return ehpads
            .filter(e => !selectedTournee || e.tournee === selectedTournee.name)
            .sort((a, b) => a.nom.localeCompare(b.nom));
    }, [ehpads, selectedTourneeId, tournees]);

    const byEhpad = useMemo(() => {
        return historique.reduce((acc, bon) => {
            const key = bon.ehpadId;
            if (!acc[key]) acc[key] = { nom: bon.ehpadNom, code: bon.ehpadCode, tourneeName: bon.tourneeName, bons: [] };
            acc[key].bons.push(bon);
            return acc;
        }, {});
    }, [historique]);

    const resetToFirstPage = () => setHistoriquePage(0);

    const handleTourneeChange = (v) => {
        setSelectedTourneeId(v);
        setSelectedEhpadId(null);
        resetToFirstPage();
    };

    const hasActiveFilters = Boolean(selectedTourneeId || selectedEhpadId || selectedDateDebut || selectedDateFin);

    const resetFilters = () => {
        setSelectedTourneeId(null);
        setSelectedEhpadId(null);
        setSelectedDateDebut(null);
        setSelectedDateFin(null);
        resetToFirstPage();
    };

    return (
        <div className="bl-tab">
            <div className="bl-filters">
                <label className="bl-filters__field" htmlFor="bl-history-tournee">
                    <span>Tournée</span>
                    <select
                        id="bl-history-tournee"
                        value={selectedTourneeId || ""}
                        onChange={e => handleTourneeChange(e.target.value ? Number(e.target.value) : null)}
                    >
                        <option value="">Toutes</option>
                        {tournees.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </label>
                <label className="bl-filters__field" htmlFor="bl-history-ehpad">
                    <span>EHPAD</span>
                    <select
                        id="bl-history-ehpad"
                        value={selectedEhpadId || ""}
                        onChange={e => { setSelectedEhpadId(e.target.value ? Number(e.target.value) : null); resetToFirstPage(); }}
                    >
                        <option value="">Tous</option>
                        {availableEhpadsInFilter.map(e => <option key={e.id} value={e.id}>{e.nom}</option>)}
                    </select>
                </label>
                <label className="bl-filters__field" htmlFor="bl-history-date-debut">
                    <span>Du</span>
                    <input
                        id="bl-history-date-debut"
                        type="date"
                        value={selectedDateDebut || ""}
                        onChange={e => { setSelectedDateDebut(e.target.value || null); resetToFirstPage(); }}
                    />
                </label>
                <label className="bl-filters__field" htmlFor="bl-history-date-fin">
                    <span>Au</span>
                    <input
                        id="bl-history-date-fin"
                        type="date"
                        value={selectedDateFin || ""}
                        onChange={e => { setSelectedDateFin(e.target.value || null); resetToFirstPage(); }}
                    />
                </label>
                <div className="bl-filters__tools">
                    {hasActiveFilters && (
                        <button type="button" className="bl-link-button" onClick={resetFilters}>Réinitialiser</button>
                    )}
                    <button type="button" className="bl-icon-button bl-icon-button--bordered" onClick={refresh} title="Actualiser" aria-label="Actualiser" disabled={loading}>↺</button>
                </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
                    <h2 style={{ margin: 0, fontSize: "18px", color: "#1e293b" }}>
                        <span>Historique des livraisons</span>
                        <span style={{ fontSize: "14px", fontWeight: 400, color: "#94a3b8", marginLeft: "10px" }}>
                            {totalHistoriqueElements} bon{totalHistoriqueElements > 1 ? "s" : ""}
                        </span>
                    </h2>
                </div>

                {loading && <div style={emptyState}>Chargement…</div>}
                {!loading && historique.length === 0 && (
                    <div style={emptyState}>Aucun bon de livraison dans l'historique pour les filtres sélectionnés.</div>
                )}

                {!loading && Object.entries(byEhpad).map(([ehpadId, group]) => (
                    <div key={ehpadId} style={{ marginBottom: "32px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                            <div style={{ fontWeight: 700, fontSize: "15px", color: "#1e293b" }}>{group.nom}</div>
                            <div style={{ fontSize: "12px", color: "#94a3b8", background: "#f1f5f9", borderRadius: "6px", padding: "2px 8px" }}>
                                {group.code}
                            </div>
                            {group.tourneeName && (
                                <div style={{ fontSize: "12px", color: "#94a3b8" }}>{group.tourneeName}</div>
                            )}
                            <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                                {group.bons.length} livraison{group.bons.length > 1 ? "s" : ""}
                            </div>
                        </div>
                        {group.bons.map(bon => (
                            <BonHistoriqueCard key={bon.id} bon={bon} pharmacy={pharmacy} />
                        ))}
                    </div>
                ))}

                {!loading && totalHistoriquePages > 1 && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", marginTop: "8px" }}>
                        <button
                            type="button"
                            onClick={() => setHistoriquePage(page => Math.max(0, page - 1))}
                            disabled={historiquePage === 0}
                            style={{ ...btnSecondary, opacity: historiquePage === 0 ? 0.5 : 1 }}
                        >
                            Précédent
                        </button>
                        <span style={{ color: "#64748b", fontSize: "13px", fontWeight: 700 }}>
                            Page {historiquePage + 1} / {totalHistoriquePages}
                        </span>
                        <button
                            type="button"
                            onClick={() => setHistoriquePage(page => Math.min(totalHistoriquePages - 1, page + 1))}
                            disabled={historiquePage >= totalHistoriquePages - 1}
                            style={{ ...btnSecondary, opacity: historiquePage >= totalHistoriquePages - 1 ? 0.5 : 1 }}
                        >
                            Suivant
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ─── Page principale avec onglets ────────────────────────────── */
export default function BonLivraison() {
    const { selectedPharmacy } = useAuth() || {};
    const location = useLocation();
    const navigate = useNavigate();
    const [pharmacy, setPharmacy] = useState(null);

    const tabs = [
        { key: "enCours", label: "Bons en cours", path: "/bon-livraison" },
        { key: "historique", label: "Historique", path: "/bon-livraison/historique" },
        { key: "planning", label: "Planning livraison", path: "/bon-livraison/planning" },
    ];
    const activeTab = bonLivraisonTabFromPath(location.pathname);

    useEffect(() => {
        setPharmacy(selectedPharmacy ?? null);
        API.get("/admin/pharmacy", { noRedirectOn401: true })
            .then(r => setPharmacy(r.data ?? selectedPharmacy ?? null))
            .catch(() => setPharmacy(selectedPharmacy ?? null));
    }, [selectedPharmacy]);

    const effectivePharmacy = pharmacy || selectedPharmacy;

    return (
        <div className="bl-page">
            <header className="bl-page-header">
                <nav className="bl-tabs" aria-label="Sections du bon de livraison">
                    {tabs.map(tab => (
                        <button
                            type="button"
                            key={tab.key}
                            onClick={() => navigate(tab.path)}
                            className={activeTab === tab.key ? "bl-tab-link is-active" : "bl-tab-link"}
                            aria-current={activeTab === tab.key ? "page" : undefined}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </header>

            <div className="bl-page-body">
                {activeTab === "enCours"    && <BonsEnCoursTab pharmacy={effectivePharmacy} />}
                {activeTab === "historique" && <HistoriqueTab  pharmacy={effectivePharmacy} />}
                {activeTab === "planning"   && (
                    <div className="bl-planning">
                        <div className="bl-planning__inner">
                            <DeliveryPlanningTab />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

const idType = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);

const pharmacyShape = PropTypes.shape({
    name: PropTypes.string,
    nom: PropTypes.string,
    address: PropTypes.string,
    phone: PropTypes.string,
});

const medicamentShape = PropTypes.shape({
    id: idType.isRequired,
    lineKey: PropTypes.string,
    lineKind: PropTypes.string,
    nom: PropTypes.string,
    dosage: PropTypes.string,
    quantite: PropTypes.number,
    statut: PropTypes.string,
    destination: PropTypes.string,
    patientNom: PropTypes.string,
    patientPrenom: PropTypes.string,
    patientAdresse: PropTypes.string,
    patientTelephone: PropTypes.string,
    classificationMedicament: PropTypes.string,
});

const ehpadBonShape = PropTypes.shape({
    ehpadId: idType,
    ehpadCode: PropTypes.string,
    ehpadNom: PropTypes.string,
    ehpadAddress: PropTypes.string,
    ehpadPhone: PropTypes.string,
    tourneeId: idType,
    tourneeName: PropTypes.string,
    isStup: PropTypes.bool,
    medicaments: PropTypes.arrayOf(medicamentShape).isRequired,
});

const bonHistoriqueShape = PropTypes.shape({
    id: idType.isRequired,
    dateLivraison: PropTypes.string,
    ehpadId: idType,
    ehpadCode: PropTypes.string,
    ehpadNom: PropTypes.string,
    tourneeId: idType,
    tourneeName: PropTypes.string,
    commentaireLivreur: PropTypes.string,
    nombreCaisses: PropTypes.number,
    medicaments: PropTypes.arrayOf(medicamentShape).isRequired,
});

const optionShape = PropTypes.shape({
    id: idType.isRequired,
    name: PropTypes.string,
    nom: PropTypes.string,
});

EhpadPanelHeader.propTypes = {
    ehpad: ehpadBonShape.isRequired,
    hasStupVariant: PropTypes.bool.isRequired,
    onBack: PropTypes.func.isRequired,
    onSelectVariant: PropTypes.func.isRequired,
};

BonEnCoursCard.propTypes = {
    ehpad: ehpadBonShape.isRequired,
    onOpen: PropTypes.func.isRequired,
};

PrintPreviewPanel.propTypes = {
    ehpad: ehpadBonShape.isRequired,
    pharmacy: pharmacyShape,
    groupingMode: PropTypes.oneOf(PRINT_GROUPING_MODES.map(mode => mode.key)).isRequired,
    onClose: PropTypes.func.isRequired,
};

HeaderCheckbox.propTypes = {
    label: PropTypes.string.isRequired,
    checked: PropTypes.bool.isRequired,
    indeterminate: PropTypes.bool.isRequired,
    disabled: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
};

MedicamentTable.propTypes = {
    medicaments: PropTypes.arrayOf(medicamentShape).isRequired,
    onMarkNonLivre: PropTypes.func,
    includeById: PropTypes.objectOf(PropTypes.bool),
    onToggleInclude: PropTypes.func,
    onToggleAllIncluded: PropTypes.func,
    selectedDeliveryIds: PropTypes.instanceOf(Set),
    onToggleDelivery: PropTypes.func,
    onToggleAllDelivery: PropTypes.func,
};

SelectionToast.propTypes = {
    selectedCount: PropTypes.number.isRequired,
    toTraiteCount: PropTypes.number.isRequired,
    toLivreCount: PropTypes.number.isRequired,
    toProductionCount: PropTypes.number.isRequired,
    busy: PropTypes.bool.isRequired,
    onTraiter: PropTypes.func.isRequired,
    onLivrer: PropTypes.func.isRequired,
    onRenvoyer: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
};

NoticeBanner.propTypes = {
    notice: PropTypes.shape({
        tone: PropTypes.oneOf(["info", "success", "error"]).isRequired,
        message: PropTypes.string.isRequired,
        undo: PropTypes.shape({
            items: PropTypes.arrayOf(medicamentShape).isRequired,
            patchBuilder: PropTypes.func.isRequired,
        }),
    }).isRequired,
    busy: PropTypes.bool.isRequired,
    onUndo: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
};

ReturnToProductionModal.propTypes = {
    medicaments: PropTypes.arrayOf(medicamentShape).isRequired,
    busy: PropTypes.bool.isRequired,
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

DeliveryConfirmModal.propTypes = {
    target: PropTypes.shape({
        ehpad: ehpadBonShape.isRequired,
        ids: PropTypes.arrayOf(idType).isRequired,
        cnoIds: PropTypes.arrayOf(idType),
        commentaireLivreur: PropTypes.string,
        labelDate: PropTypes.string,
        labelCode: PropTypes.string,
        labelBoxCount: idType,
        deliveryPlanningId: idType,
        deliveryOccurrenceDate: PropTypes.string,
    }).isRequired,
    pharmacy: pharmacyShape,
    deliveryOptions: PropTypes.arrayOf(PropTypes.shape({
        planningId: idType.isRequired,
        occurrenceDate: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    })).isRequired,
    sending: PropTypes.bool.isRequired,
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onTargetChange: PropTypes.func.isRequired,
};

DeliveryModalDetail.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    last: PropTypes.bool,
};

FilterBarEnCours.propTypes = {
    availableTournees: PropTypes.arrayOf(optionShape).isRequired,
    selectedTourneeId: idType,
    setSelectedTourneeId: PropTypes.func.isRequired,
    availableEhpads: PropTypes.arrayOf(optionShape).isRequired,
    selectedEhpadId: idType,
    setSelectedEhpadId: PropTypes.func.isRequired,
    selectedStatuts: PropTypes.arrayOf(PropTypes.string).isRequired,
    setSelectedStatuts: PropTypes.func.isRequired,
    showScopeFilters: PropTypes.bool.isRequired,
    searchQuery: PropTypes.string.isRequired,
    setSearchQuery: PropTypes.func.isRequired,
    searchInputRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
    searchResultCount: PropTypes.number.isRequired,
    hasActiveFilters: PropTypes.bool.isRequired,
    onReset: PropTypes.func.isRequired,
    onRefresh: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    printTools: PropTypes.node,
};

BonsEnCoursTab.propTypes = {
    pharmacy: pharmacyShape,
};

BonHistoriqueCard.propTypes = {
    bon: bonHistoriqueShape.isRequired,
    pharmacy: pharmacyShape,
};

HistoriqueTab.propTypes = {
    pharmacy: pharmacyShape,
};
